// ============================================================================
// JARVISTRAVEL - MAP LINK
// Opens a stop or stay in the traveller's maps APP, not a web page (JAR-2701).
//
//   - computer:      the web map, in a new tab, as before.
//   - Android:       a geo: link, opened in place, so the OS picks the maps app.
//   - iPhone / iPad: the first tap offers Apple Maps, Google Maps or Waze; the
//                    pick is remembered on the device, so the next tap opens
//                    that app straight away. Press and hold to switch app.
//
// Every opener (the itinerary pins, the Property address) renders this, so the
// behaviour lives in one place. The link maths is pure, in utils/maps.ts.
// ============================================================================

import { useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from 'react';
import { MapPin } from 'lucide-react';
import { Modal } from './Modal';
import { safeHref } from '@/app/utils/safeUrl';
import {
  type MapLinkOptions,
  type MapsProvider,
  MAPS_PROVIDERS,
  mapPlatform,
  getMapLink,
  appMapUrl,
  geoMapUrl,
  getStoredMapsProvider,
  storeMapsProvider,
} from '@/app/utils/maps';

export interface MapLinkProps {
  location: MapLinkOptions;
  /** Accessible name. Required for an icon-only trigger (no visible text);
   *  omit when `children` already render the name as visible text. */
  label?: string;
  /** Trigger content. Defaults to a round map-pin icon button. */
  children?: ReactNode;
  /** Trigger classes. Defaults to the round icon-button look. */
  className?: string;
  size?: 'sm' | 'md';
  /** Runs before navigation (e.g. stopPropagation inside a clickable row). */
  onClick?: (e: MouseEvent) => void;
}

const HOLD_MS = 500;
const NO_CALLOUT: CSSProperties = { WebkitTouchCallout: 'none' };

/** The iPhone / iPad chooser: three apps, each a universal link opened in
 *  place. Picking one remembers it for next time. */
function MapsPicker({ location, onClose }: { location: MapLinkOptions; onClose: () => void }) {
  return (
    <Modal isOpen onClose={onClose} variant="sheet" size="sm" title="Open in Maps">
      <div className="flex flex-col gap-2 pb-2">
        {MAPS_PROVIDERS.map(({ id, label }) => (
          <a
            key={id}
            href={safeHref(appMapUrl(location, id))}
            onClick={() => { storeMapsProvider(id); onClose(); }}
            className="flex items-center gap-3 rounded-xl px-4 py-3 text-base font-medium text-ink bg-surface hover:bg-surface-raised active:opacity-70"
          >
            <MapPin className="w-5 h-5 text-accent shrink-0" strokeWidth={2} aria-hidden="true" />
            {label}
          </a>
        ))}
      </div>
    </Modal>
  );
}

export function MapLink({ location, label, children, className, size = 'sm', onClick }: MapLinkProps) {
  const [picking, setPicking] = useState(false);
  const holdFired = useRef(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const platform = mapPlatform();
  const remembered = platform === 'ios' ? getStoredMapsProvider() : null;

  const iconSize = size === 'md' ? 'w-4 h-4' : 'w-3.5 h-3.5';
  const defaultClass = `inline-flex items-center justify-center rounded-full transition-colors text-accent bg-accent/10 hover:bg-accent/15 ${size === 'md' ? 'w-9 h-9' : 'w-8 h-8'}`;
  const triggerClass = className ?? defaultClass;
  const content = children ?? <MapPin className={iconSize} strokeWidth={2} aria-hidden="true" />;
  // An icon-only trigger has no visible text, so it needs an accessible name.
  const ariaLabel = label ?? (children ? undefined : 'Open in Maps');

  // iPhone / iPad, nothing chosen yet: the trigger opens the picker.
  if (platform === 'ios' && !remembered) {
    return (
      <>
        <button
          type="button"
          onClick={(e) => { onClick?.(e); if (!e.defaultPrevented) setPicking(true); }}
          aria-label={ariaLabel ?? 'Open in Maps'}
          title={label ?? 'Open in Maps'}
          className={triggerClass}
        >
          {content}
        </button>
        {picking && <MapsPicker location={location} onClose={() => setPicking(false)} />}
      </>
    );
  }

  // Everyone else navigates through a real link:
  //   web     → the web map in a new tab (as before)
  //   android → a geo: link, in place, so the OS hands it to a maps app
  //   ios      → the remembered app's universal link, in place
  const web = platform === 'web';
  const href = web
    ? getMapLink(location).url
    : platform === 'android'
      ? geoMapUrl(location)
      : appMapUrl(location, remembered as MapsProvider);

  // iOS only: press and hold re-opens the picker so the chosen app can be
  // changed. NO_CALLOUT stops iOS showing its own link preview on the hold.
  // ponytail: hold-to-switch is the only change path; add a visible control if
  // travellers miss it (follow-up).
  const cancelHold = () => { if (holdTimer.current) { clearTimeout(holdTimer.current); holdTimer.current = null; } };
  const holdProps = platform === 'ios'
    ? {
        onPointerDown: () => {
          holdFired.current = false;
          holdTimer.current = setTimeout(() => { holdFired.current = true; setPicking(true); }, HOLD_MS);
        },
        onPointerUp: cancelHold,
        onPointerLeave: cancelHold,
        onPointerCancel: cancelHold,
        style: NO_CALLOUT,
      }
    : {};

  return (
    <>
      <a
        href={safeHref(href)}
        {...(web ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        {...holdProps}
        onClick={(e: MouseEvent) => {
          if (holdFired.current) { holdFired.current = false; e.preventDefault(); return; }
          onClick?.(e);
        }}
        aria-label={ariaLabel}
        title={label ?? (children ? undefined : platform === 'ios' ? 'Open in Maps (press and hold to switch app)' : 'Open in Maps')}
        className={triggerClass}
      >
        {content}
      </a>
      {picking && <MapsPicker location={location} onClose={() => setPicking(false)} />}
    </>
  );
}
